"use client";
import { useEffect, useRef } from "react";
import { useInView } from "react-intersection-observer";

interface InfiniteScrollTriggerProps {
  onLoadMore: () => void;
  hasMore: boolean;
  isLoading: boolean;
  threshold?: number;
  rootMargin?: string;
}

const InfiniteScrollTrigger = ({
  onLoadMore,
  hasMore,
  isLoading,
  threshold = 0,
  rootMargin = "0px 0px 1200px 0px",
}: InfiniteScrollTriggerProps) => {
  const isLoadingRef = useRef(false);
  const lastTriggeredRef = useRef(false);

  const { ref, inView } = useInView({
    threshold,
    rootMargin,
    triggerOnce: false,
  });

  // Update loading ref
  useEffect(() => {
    isLoadingRef.current = isLoading;
  }, [isLoading]);

  // Handle trigger
  useEffect(() => {
    if (!inView) {
      lastTriggeredRef.current = false;
      return;
    }

    if (lastTriggeredRef.current || !hasMore || isLoadingRef.current) {
      return;
    }

    lastTriggeredRef.current = true;
    onLoadMore();
  }, [inView, hasMore, onLoadMore]);

  // Reset trigger when loading completes
  useEffect(() => {
    if (!isLoading && lastTriggeredRef.current) {
      lastTriggeredRef.current = false;
    }
  }, [isLoading]);

  if (!hasMore) return null;

  return (
    <div
      ref={ref}
      className="w-full h-2"
      role="status"
      aria-live="polite"
      aria-busy={isLoading}
      aria-label={hasMore ? "Daha fazla ürün yüklenecek" : "Tüm ürünler yüklendi"}
    />
  );
};

export default InfiniteScrollTrigger;
